import produce from 'immer';
import { isEmpty } from 'lodash';
import { StateCreator } from 'zustand';

import guarantorsAPI from '../services/api/guarantors';
import { IGuarantorDocument } from '../shared/ts/document';
import { IGuarantor } from '../shared/ts/guarantor';

export interface IGuarantorsSlice {
  guarantors: IGuarantor[];
  getBookingGuarantors: (bookingId: string) => Promise<void>;
  postGuarantor: (
    bookingId: string,
    firstName: string,
    lastName: string,
    occupation: string,
    isMyself: boolean,
  ) => Promise<number>;
  deleteGuarantor: (id: number) => Promise<void>;
  getGuarantorDocuments: (id: number) => Promise<void>;
  postGuarantorDocument: (
    payload: IGuarantorDocument,
    onUploadProgress: (progressEvent: ProgressEvent) => void,
  ) => Promise<void>;
  deleteGuarantorDocument: (
    guarantorId: number,
    id: number,
    documentSide?: string,
  ) => Promise<void>;
}
const createGuarantorsSlice: StateCreator<IGuarantorsSlice, [], []> = (
  set,
  get,
) => ({
  guarantors: [],
  //guarantors
  getBookingGuarantors: async (bookingId: string) => {
    try {
      set({ guarantors: await guarantorsAPI.getBookingGuarantors(bookingId) });
    } catch (err) {
      throw err;
    }
  },
  postGuarantor: async (
    bookingId: string,
    firstName: string,
    lastName: string,
    occupation: string,
    isMyself: boolean,
  ) => {
    try {
      const newGuarantor = await guarantorsAPI.postGuarantor(
        {
          booking_id: bookingId,
          first_name: firstName,
          last_name: lastName,
          occupation,
        },
        isMyself,
      );

      set(state => ({
        guarantors: [...state.guarantors, { ...newGuarantor, documents: [] }],
      }));

      return newGuarantor.id;
    } catch (err) {
      throw err;
    }
  },
  deleteGuarantor: async (id: number) => {
    try {
      await guarantorsAPI.deleteGuarantor(id);

      const guarantorToDeleteIndex = get().guarantors.findIndex(
        guarantor => guarantor.id === id,
      );

      set(
        produce(state => {
          state.guarantors.splice(guarantorToDeleteIndex, 1);
        }),
      );
    } catch (err) {
      throw err;
    }
  },
  //guarantor documents
  getGuarantorDocuments: async (id: number) => {
    try {
      const documents = await guarantorsAPI.getGuarantorDocuments(id);

      const guarantorToUpdateIndex = get().guarantors.findIndex(
        guarantor => guarantor.id === id,
      );

      console.log(documents, guarantorToUpdateIndex);
      set(
        produce(state => {
          state.guarantors[guarantorToUpdateIndex].documents = documents;
        }),
      );
    } catch (err) {
      throw err;
    }
  },
  postGuarantorDocument: async (
    payload: IGuarantorDocument,
    onUploadProgress: (progressEvent: ProgressEvent) => void,
  ) => {
    try {
      const { guarantor_id, ...document } =
        await guarantorsAPI.postGuarantorDocument(payload, onUploadProgress);

      const guarantorToUpdateIndex = get().guarantors.findIndex(
        guarantor => guarantor.id === guarantor_id,
      );

      const documentToUpdateIndex = get().guarantors[
        guarantorToUpdateIndex
      ].documents?.findIndex(doc => doc.id === document.id);

      set(
        produce(state => {
          if (documentToUpdateIndex !== -1) {
            state.guarantors[guarantorToUpdateIndex].documents?.splice(
              documentToUpdateIndex!,
              1,
              document,
            );
          } else
            state.guarantors[guarantorToUpdateIndex].documents?.push(document);
        }),
      );
    } catch (err) {
      throw err;
    }
  },
  deleteGuarantorDocument: async (
    guarantorId: number,
    id: number,
    documentSide?: string,
  ) => {
    try {
      const document = await guarantorsAPI.deleteGuarantorDocument(
        id,
        documentSide,
      );

      const guarantorToUpdateIndex = get().guarantors.findIndex(
        (guarantor: IGuarantor) => guarantor.id === guarantorId,
      );

      const isDocumentToUpdate = !isEmpty(document);
      const documentIndex = get().guarantors[
        guarantorToUpdateIndex
      ].documents?.findIndex(doc => doc.id === id);

      set(
        produce(state => {
          if (isDocumentToUpdate) {
            state.guarantors[guarantorToUpdateIndex].documents?.splice(
              documentIndex!,
              1,
              {
                id,
                ...document,
              },
            );
          } else
            state.guarantors[guarantorToUpdateIndex].documents?.splice(
              documentIndex!,
              1,
            );
        }),
      );
    } catch (err) {
      throw err;
    }
  },
});

export default createGuarantorsSlice;
